import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { featureAPI, taskAPI, projectAPI, aiAPI } from '../../../api/services';
import FeatureBatchEstimation from './FeatureBatchEstimation';
import { Feature, FeatureStatus, FeatureApprovalStatus, FeatureBlocker, FeatureMember, AmendmentRecord, BreachRecord, User } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchSprintsByProject, Sprint } from '../../../store/sprintSlice';
import FeatureForm from './FeatureForm';
import AmendmentLog from '../../Common/AmendmentLog';
import BreachHistory from '../../Common/BreachHistory';

const STATUS_COLORS: Record<FeatureStatus, string> = {
  planning: 'bg-gray-100 text-gray-700',
  development: 'bg-blue-100 text-blue-700',
  testing: 'bg-purple-100 text-purple-700',
  done: 'bg-green-100 text-green-700',
  deployed: 'bg-teal-100 text-teal-700',
  archived: 'bg-gray-200 text-gray-400',
};

const APPROVAL_COLORS: Record<FeatureApprovalStatus, string> = {
  not_required: '',
  pending_approval: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  disputed: 'bg-orange-100 text-orange-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

const TASK_STATUS_COLORS: Record<string, string> = {
  todo: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  review: 'bg-purple-100 text-purple-700',
  done: 'bg-green-100 text-green-700',
  blocked: 'bg-red-100 text-red-700',
};

const STATUS_FLOW: FeatureStatus[] = ['planning', 'development', 'testing', 'done', 'deployed', 'archived'];

const BLOCKER_TYPE_COLORS: Record<string, string> = {
  task: 'bg-blue-100 text-blue-700',
  feature: 'bg-purple-100 text-purple-700',
  bug: 'bg-red-100 text-red-700',
  external: 'bg-orange-100 text-orange-700',
};

const MEMBER_ROLE_COLORS: Record<string, string> = {
  owner: 'bg-blue-100 text-blue-700',
  contributor: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
};

interface Props {
  featureIdProp?: string;
  projectId?: string;
  onBack?: () => void;
}

// ─── Expandable Task Row with subtasks ────────────────────────────────────────

const TaskRow: React.FC<{ task: any; onRefresh: () => void; featureId: string; featureApproved?: boolean }> = ({ task: initialTask, onRefresh, featureId, featureApproved = false }) => {
  const navigate = useNavigate();
  const [task, setTask] = useState(initialTask);
  const [expanded, setExpanded] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(initialTask.description || '');
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; newStatus: string; isDone: boolean; isRevert: boolean }>({ show: false, newStatus: '', isDone: false, isRevert: false });
  const [taskError, setTaskError] = useState<string | null>(null);

  // Sync if parent data changes (e.g. after background refresh)
  useEffect(() => { setTask(initialTask); }, [initialTask]);

  const subtasks = task.subtasks ?? [];
  const completedCount = subtasks.filter((s: any) => s.completed).length;
  const hasSubtasks = subtasks.length > 0;

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subtaskTitle.trim()) return;
    setCreating(true);
    try {
      const res = await taskAPI.createSubtask(task._id, subtaskTitle.trim());
      const updated = res.data.data ?? res.data;
      setTask(updated);
      setSubtaskTitle('');
      setAddingSubtask(false);
    } catch { /* ignore */ }
    setCreating(false);
  };

  const handleToggleSubtask = async (subtaskId: string, completed: boolean) => {
    // Optimistic update
    setTask((prev: any) => ({
      ...prev,
      subtasks: (prev.subtasks ?? []).map((st: any) =>
        st._id === subtaskId ? { ...st, completed: !completed } : st
      ),
    }));
    try {
      await taskAPI.updateSubtask(task._id, subtaskId, !completed);
    } catch {
      // Revert on error
      setTask((prev: any) => ({
        ...prev,
        subtasks: (prev.subtasks ?? []).map((st: any) =>
          st._id === subtaskId ? { ...st, completed } : st
        ),
      }));
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    const prev = task.subtasks;
    setTask((t: any) => ({ ...t, subtasks: (t.subtasks ?? []).filter((st: any) => st._id !== subtaskId) }));
    try {
      await taskAPI.deleteSubtask(task._id, subtaskId);
    } catch {
      setTask((t: any) => ({ ...t, subtasks: prev }));
    }
  };

  const handleRenameSubtask = async (subtaskId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    const updated = (task.subtasks ?? []).map((st: any) => st._id === subtaskId ? { ...st, title: newTitle.trim() } : st);
    setTask((t: any) => ({ ...t, subtasks: updated }));
    setEditingId(null);
    try {
      await taskAPI.updateTask(task._id, { subtasks: updated } as any);
    } catch { /* ignore */ }
  };

  const showTaskError = (msg: string) => {
    setTaskError(msg);
    setTimeout(() => setTaskError(null), 5000);
  };

  const handleQuickStatus = async (newStatus: string) => {
    if (newStatus === 'done' && !featureApproved) {
      showTaskError('Cannot mark as done — feature must be approved first.');
      return;
    }
    // Show confirmation for done/revert
    if (newStatus === 'done' || (task.status === 'done' && newStatus !== 'done')) {
      setConfirmModal({
        show: true,
        newStatus,
        isDone: newStatus === 'done',
        isRevert: task.status === 'done' && newStatus !== 'done',
      });
      return;
    }
    // Other status changes — no confirmation needed
    const oldStatus = task.status;
    setTask((t: any) => ({ ...t, status: newStatus }));
    try { await taskAPI.updateStatus(task._id, newStatus); }
    catch (err: any) { setTask((t: any) => ({ ...t, status: oldStatus })); if (err.response?.data?.message) showTaskError(err.response.data.message); }
  };

  const confirmStatusChange = async () => {
    const { newStatus } = confirmModal;
    const oldStatus = task.status;
    setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false });
    setTask((t: any) => ({ ...t, status: newStatus }));
    try { await taskAPI.updateStatus(task._id, newStatus); }
    catch (err: any) { setTask((t: any) => ({ ...t, status: oldStatus })); if (err.response?.data?.message) showTaskError(err.response.data.message); }
  };

  const handleSaveDesc = async () => {
    setTask((t: any) => ({ ...t, description: descValue }));
    setEditingDesc(false);
    try {
      await taskAPI.updateTask(task._id, { description: descValue } as any);
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Main task row */}
      <div className="px-4 py-3 flex items-center gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-5 h-5 text-gray-400 hover:text-gray-600 shrink-0 flex items-center justify-center"
        >
          <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          onClick={() => handleQuickStatus(task.status === 'done' ? 'todo' : 'done')}
          className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
            task.status === 'done' ? 'border-green-500 bg-green-500' :
            task.status === 'in_progress' ? 'border-blue-500' :
            task.status === 'blocked' ? 'border-red-500' :
            'border-gray-300'
          }`}
          title={task.status === 'done' ? 'Mark incomplete' : 'Mark done'}
        >
          {task.status === 'done' && (
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          )}
        </button>

        <span
          onClick={() => navigate(`/employee/tasks/${task._id}`)}
          className={`text-sm font-medium truncate flex-1 cursor-pointer hover:text-blue-600 ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900'}`}
          title={`${task.title || task.name}${task.description ? '\n' + task.description : ''}`}
        >
          {task.title || task.name}
        </span>

        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${PRIORITY_COLORS[task.priority] || 'bg-gray-100 text-gray-600'}`}>
          {task.priority}
        </span>
        {task.storyPoints > 0 && (
          <span className="text-xs text-blue-600 font-medium shrink-0">{task.storyPoints} SP</span>
        )}
        {!task.storyPoints && (
          <span className="text-[10px] text-red-500 shrink-0">No SP</span>
        )}
        {task.needsBatchApproval && !task.batchId && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">needs batch</span>
        )}
        {hasSubtasks && (
          <span className="text-xs text-gray-400 shrink-0">{completedCount}/{subtasks.length}</span>
        )}
        {task.assignedTo?.[0]?.name && (
          <span className="text-xs text-gray-400 shrink-0">{task.assignedTo[0].name}</span>
        )}
      </div>

      {/* Inline error banner */}
      {taskError && (
        <div className="mx-4 mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          <span className="text-xs text-red-700 flex-1">{taskError}</span>
          <button onClick={() => setTaskError(null)} className="text-red-400 hover:text-red-600 shrink-0">&times;</button>
        </div>
      )}

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          {/* Task description — inline editable */}
          <div className="px-4 py-2 ml-5 border-b border-gray-100">
            {editingDesc ? (
              <div className="space-y-1">
                <textarea
                  autoFocus
                  value={descValue}
                  onChange={e => setDescValue(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <div className="flex gap-1">
                  <button onClick={handleSaveDesc} className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700">Save</button>
                  <button onClick={() => { setEditingDesc(false); setDescValue(task.description || ''); }} className="text-xs text-gray-500">Cancel</button>
                </div>
              </div>
            ) : (
              <p
                onClick={() => setEditingDesc(true)}
                className="text-xs text-gray-500 cursor-pointer hover:text-gray-700"
                title="Click to edit description"
              >
                {task.description || 'No description — click to add'}
              </p>
            )}
          </div>

          {/* Subtasks */}
          <div className="px-4 py-2">
            {subtasks.length > 0 && (
              <div className="space-y-0.5 mb-2">
                {subtasks.map((st: any) => (
                  <div key={st._id} className="flex items-center gap-2 py-1.5 ml-5 group hover:bg-white rounded px-1 -mx-1">
                    <input
                      type="checkbox"
                      checked={st.completed}
                      onChange={() => handleToggleSubtask(st._id, st.completed)}
                      className="w-3.5 h-3.5 text-blue-600 rounded shrink-0"
                    />
                    {editingId === st._id ? (
                      <input
                        autoFocus
                        value={editingTitle}
                        onChange={e => setEditingTitle(e.target.value)}
                        onBlur={() => handleRenameSubtask(st._id, editingTitle)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameSubtask(st._id, editingTitle); if (e.key === 'Escape') setEditingId(null); }}
                        className="flex-1 border border-blue-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <span
                        onDoubleClick={() => { setEditingId(st._id); setEditingTitle(st.title); }}
                        className={`text-xs flex-1 cursor-pointer ${st.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}
                        title="Double-click to edit"
                      >
                        {st.title}
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteSubtask(st._id)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0"
                      title="Delete"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {addingSubtask ? (
              <form onSubmit={handleAddSubtask} className="flex gap-2 ml-5">
                <input
                  autoFocus
                  value={subtaskTitle}
                  onChange={e => setSubtaskTitle(e.target.value)}
                  placeholder="Subtask title..."
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button type="submit" disabled={creating || !subtaskTitle.trim()} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50">
                  {creating ? '...' : 'Add'}
                </button>
                <button type="button" onClick={() => { setAddingSubtask(false); setSubtaskTitle(''); }} className="text-xs text-gray-500">Cancel</button>
              </form>
            ) : (
              <button onClick={() => setAddingSubtask(true)} className="text-xs text-blue-600 hover:text-blue-700 ml-5">
                + Add subtask
              </button>
            )}
          </div>
        </div>
      )}

      {confirmModal.show && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold text-gray-900">
              {confirmModal.isDone ? 'Complete Task' : 'Revert Task'}
            </h3>
            <p className="text-sm text-gray-600">
              {confirmModal.isDone
                ? `Mark "${task.title}" as done?`
                : `Move "${task.title}" back to ${confirmModal.newStatus.replace('_', ' ')}?`
              }
            </p>
            {confirmModal.isDone && (task.storyPoints || 0) > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700 font-medium">+{task.storyPoints} story points</p>
                <p className="text-xs text-green-600 mt-0.5">Points will be recorded in your journal.</p>
              </div>
            )}
            {confirmModal.isRevert && (task.storyPoints || 0) > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700 font-medium">-{task.storyPoints} story points will be deducted</p>
                <p className="text-xs text-yellow-600 mt-0.5">Points will be re-earned when you complete the task again.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false })} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={confirmStatusChange}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${confirmModal.isDone ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-500 hover:bg-yellow-600'}`}
              >
                {confirmModal.isDone ? 'Mark as Done \u2713' : 'Revert & Deduct'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const FeatureDetail: React.FC<Props> = ({ featureIdProp, projectId: projectIdProp, onBack }) => {
  const params = useParams<{ id: string }>();
  const featureId = featureIdProp ?? params.id;
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  const canApprove = user?.role === 'admin' || (user?.organizationRole?.priority ?? 0) >= 50;
  const currentUserId = user?.id || (user as any)?._id;
  const orgPriority = (user as any)?.organizationRole?.priority || 0;

  const [feature, setFeature] = useState<Feature | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'overview' | 'tasks' | 'blockers' | 'members'>('overview');
  const [editing, setEditing] = useState(false);

  // Tasks
  const [tasks, setTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium', description: '', storyPoints: 0 });
  const [creatingTask, setCreatingTask] = useState(false);

  // Blockers
  const [blockerForm, setBlockerForm] = useState({ type: 'external' as string, description: '' });
  const [blockerSubmitting, setBlockerSubmitting] = useState(false);

  // Members — project member search
  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRole, setMemberRole] = useState<'contributor' | 'viewer'>('contributor');
  const [memberSubmitting, setMemberSubmitting] = useState(false);

  // Sprint assignment
  const sprints = useAppSelector(s => s.sprints.sprints);
  const [showSprintPicker, setShowSprintPicker] = useState(false);
  const [sprintAssigning, setSprintAssigning] = useState(false);

  // Approval
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);
  const [editFeatureSP, setEditFeatureSP] = useState(0);
  const [aiEstimating, setAiEstimating] = useState(false);
  const [aiResult, setAiResult] = useState<{ featurePoints: number; tasks: { taskId: string; points: number; reasoning: string }[] } | null>(null);
  const [aiFallback, setAiFallback] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [disputeText, setDisputeText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const projectId = projectIdProp ?? feature?.projectId;

  const fetchFeature = useCallback(async () => {
    if (!featureId) return;
    try {
      setLoading(true);
      setError('');
      const res = await featureAPI.getById(featureId);
      const raw = res.data.data ?? res.data;
      const f = raw.feature ?? raw; // API may return { feature, tasks } or feature directly
      setFeature({
        ...f,
        blockers: f.blockers ?? [],
        members: f.members ?? [],
        tags: f.tags ?? [],
        amendmentLog: f.amendmentLog ?? [],
        breachHistory: f.breachHistory ?? [],
        stats: f.stats ?? { totalTasks: 0, completedTasks: 0, totalComments: 0 },
        dates: f.dates ?? {},
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load feature');
    } finally {
      setLoading(false);
    }
  }, [featureId]);

  const fetchTasks = useCallback(async (showSpinner = false) => {
    if (!featureId) return;
    try {
      if (showSpinner) setTasksLoading(true);
      const res = await featureAPI.getTasks(featureId);
      const payload = res.data.data ?? res.data;
      setTasks(Array.isArray(payload) ? payload : []);
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [featureId]);

  useEffect(() => { fetchFeature(); }, [fetchFeature]);
  useEffect(() => { if (tab === 'tasks') fetchTasks(true); }, [tab, fetchTasks]);

  // Load project members for the member picker + sprints for assignment
  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchSprintsByProject({ projectId }));
    projectAPI.getProjectById(projectId).then(res => {
      const proj = res.data.data ?? res.data;
      // Build a list of users from project members
      const members = (proj.members ?? []).map((m: any) => ({
        _id: m.userId,
        name: m.name,
        email: m.email || '',
      }));
      // Include owner
      if (proj.owner) {
        members.unshift({ _id: proj.owner.userId, name: proj.owner.name, email: proj.owner.email || '' });
      }
      setProjectMembers(members);
    }).catch(() => {});
  }, [projectId, dispatch]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleStatusTransition = async (newStatus: FeatureStatus) => {
    if (!featureId) return;
    setActionLoading(true);
    try {
      await featureAPI.update(featureId, { status: newStatus } as any);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!featureId) return;
    setActionLoading(true);
    try {
      await featureAPI.submitForApproval(featureId);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit for approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!featureId) return;
    setActionLoading(true);
    try {
      await featureAPI.approve(featureId);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!featureId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await featureAPI.reject(featureId, { reason: rejectReason });
      setShowRejectModal(false);
      setRejectReason('');
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignSprint = async (sprintId: string | null) => {
    if (!featureId) return;
    setSprintAssigning(true);
    try {
      await featureAPI.update(featureId, { sprintId: sprintId as any } as any);
      setShowSprintPicker(false);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign sprint');
    } finally {
      setSprintAssigning(false);
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureId || !newTask.title.trim() || !projectId) return;
    setCreatingTask(true);
    try {
      await taskAPI.createTask({
        title: newTask.title,
        description: newTask.description || newTask.title,
        priority: newTask.priority,
        storyPoints: newTask.storyPoints || 0,
        parentType: 'feature',
        parentId: featureId,
        projectId,
      } as any);
      setNewTask({ title: '', priority: 'medium', description: '', storyPoints: 0 });
      setShowCreateTask(false);
      fetchTasks();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleAddBlocker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureId || !blockerForm.description.trim()) return;
    setBlockerSubmitting(true);
    try {
      await featureAPI.addBlocker(featureId, { type: blockerForm.type, description: blockerForm.description });
      setBlockerForm({ type: 'external', description: '' });
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add blocker');
    } finally {
      setBlockerSubmitting(false);
    }
  };

  const handleResolveBlocker = async (index: number) => {
    if (!featureId) return;
    try {
      await featureAPI.resolveBlocker(featureId, index);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resolve blocker');
    }
  };

  const handleAddMember = async (user: { _id: string; name: string }) => {
    if (!featureId) return;
    setMemberSubmitting(true);
    try {
      await featureAPI.addMember(featureId, { userId: user._id, name: user.name, role: memberRole });
      setMemberSearch('');
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add member');
    } finally {
      setMemberSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!featureId) return;
    try {
      await featureAPI.removeMember(featureId, memberId);
      await fetchFeature();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && !feature) {
    return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }
  if (!feature) return <div className="text-center py-12 text-gray-500">Feature not found.</div>;

  if (editing) {
    return (
      <FeatureForm
        projectId={feature.projectId}
        featureId={feature._id}
        onCreated={() => { setEditing(false); fetchFeature(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const currentIdx = STATUS_FLOW.indexOf(feature.status);
  const nextStatuses = STATUS_FLOW.slice(currentIdx + 1);
  const existingMemberIds = feature.members.map(m => m.userId);
  const availableMembers = projectMembers.filter(u => u._id && !existingMemberIds.includes(u._id));
  const filteredMembers = memberSearch
    ? availableMembers.filter(u => u.name.toLowerCase().includes(memberSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(memberSearch.toLowerCase()))
    : availableMembers;
  const currentSprint = sprints.find(s => s._id === feature.sprintId);

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to features
        </button>
      )}

      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900">{feature.name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[feature.status]}`}>
                {feature.status}
              </span>
              {feature.approvalStatus && feature.approvalStatus !== 'not_required' && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${APPROVAL_COLORS[feature.approvalStatus]}`}>
                  {feature.approvalStatus.replace('_', ' ')}
                </span>
              )}
              <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[feature.priority]}`}>
                {feature.priority}
              </span>
              <span className="text-xs text-gray-400">W: {feature.weight}</span>

              {/* Sprint badge */}
              <button
                onClick={() => setShowSprintPicker(!showSprintPicker)}
                className={`text-xs px-2 py-0.5 rounded border cursor-pointer ${
                  currentSprint ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-dashed border-gray-300 text-gray-400 hover:border-gray-400'
                }`}
              >
                {currentSprint ? `Sprint: ${currentSprint.name}` : '+ Assign to sprint'}
              </button>
            </div>

            {/* Sprint picker dropdown */}
            {showSprintPicker && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-48 overflow-y-auto">
                <button
                  onClick={() => handleAssignSprint(null)}
                  disabled={sprintAssigning}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50 rounded"
                >
                  Remove from sprint (backlog)
                </button>
                {sprints.filter(s => s.status !== 'completed' && s.status !== 'cancelled').map(s => (
                  <button
                    key={s._id}
                    onClick={() => handleAssignSprint(s._id)}
                    disabled={sprintAssigning}
                    className={`w-full text-left px-3 py-1.5 text-sm rounded hover:bg-blue-50 ${s._id === feature.sprintId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                  >
                    {s.name} <span className="text-xs text-gray-400 capitalize">({s.status})</span>
                  </button>
                ))}
              </div>
            )}

            {/* Dates */}
            {(feature.dates.startDate || feature.dates.estimatedEndDate) && (
              <div className="flex gap-3 mt-2 text-xs text-gray-500">
                {feature.dates.startDate && <span>Start: {new Date(feature.dates.startDate).toLocaleDateString()}</span>}
                {feature.dates.estimatedEndDate && <span>Due: {new Date(feature.dates.estimatedEndDate).toLocaleDateString()}</span>}
                {feature.dates.actualEndDate && <span className="text-green-600">Completed: {new Date(feature.dates.actualEndDate).toLocaleDateString()}</span>}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap shrink-0">
            <button onClick={() => setEditing(true)} className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-50">Edit</button>

            {/* Status transitions */}
            {nextStatuses.length > 0 && (
              <select
                onChange={e => handleStatusTransition(e.target.value as FeatureStatus)}
                value=""
                disabled={actionLoading}
                className="text-xs border border-gray-200 rounded px-2 py-1.5 text-gray-600 focus:outline-none"
              >
                <option value="" disabled>Move to...</option>
                {nextStatuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}

            {/* Approval status indicator — explicit per status */}
            {feature.approvalStatus === 'disputed' && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Disputed — awaiting admin</span>
            )}
            {feature.approvalStatus === 'pending_approval' && (
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Pending approval{feature.submittedAt ? ` • ${new Date(feature.submittedAt).toLocaleDateString()}` : ''}</span>
            )}
            {feature.approvalStatus === 'approved' && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Approved</span>
            )}
            {feature.approvalStatus === 'rejected' && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Rejected</span>
            )}
            {(!feature.approvalStatus || feature.approvalStatus === 'not_required') && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">Not submitted</span>
            )}

            {/* Submit button — only for not_required/rejected/no status */}
            {(!feature.approvalStatus || feature.approvalStatus === 'not_required' || feature.approvalStatus === 'rejected') && (
              <button onClick={async () => {
                await fetchTasks();
                setEditFeatureSP(feature.storyPoints ?? 0);
                setAiFallback(false);
                setAiMessage('');
                setDisputeText('');
                setShowApprovalPanel(true);

                // If AI estimation already stored and task count hasn't changed, use cached
                const storedAi = feature.aiEstimation;
                const currentTaskCount = tasks.length; // tasks were just fetched above
                if (storedAi && storedAi.tasks?.length > 0 && storedAi.tasks.length === currentTaskCount) {
                  setAiResult(storedAi as any);
                  setEditFeatureSP(storedAi.featurePoints);
                  setTasks((prev: any[]) => prev.map((t: any) => {
                    const aiTask = (storedAi.tasks ?? []).find((at: any) => at.taskId === t._id);
                    return aiTask ? { ...t, _editSP: aiTask.points, _aiReasoning: aiTask.reasoning } : t;
                  }));
                  return;
                }

                // Task count changed or no stored estimation — call AI
                setAiResult(null);
                setAiEstimating(true);
                try {
                  const res = await aiAPI.estimateStoryPoints(featureId!);
                  const d = res.data;
                  if (d.fallback) {
                    setAiFallback(true);
                    setAiMessage(d.message || 'AI unavailable');
                  } else if (d.data) {
                    setAiResult(d.data);
                    setEditFeatureSP(d.data.featurePoints);
                    setTasks((prev: any[]) => prev.map((t: any) => {
                      const aiTask = d.data.tasks.find((at: any) => at.taskId === t._id);
                      return aiTask ? { ...t, _editSP: aiTask.points, _aiReasoning: aiTask.reasoning } : t;
                    }));
                  }
                } catch {
                  setAiFallback(true);
                  setAiMessage('AI estimation failed');
                }
                setAiEstimating(false);
              }} disabled={actionLoading} className="text-xs bg-yellow-500 text-white px-3 py-1.5 rounded hover:bg-yellow-600 disabled:opacity-50">
                Submit for Approval
              </button>
            )}
            {feature.approvalStatus === 'pending_approval' && canApprove && (() => {
              const isSubmitter = feature.submittedBy && currentUserId && String(feature.submittedBy) === String(currentUserId);
              if (isSubmitter && orgPriority < 50) return (
                <span className="text-xs text-gray-400 italic">You cannot approve your own submission</span>
              );
              return (<>
                <button onClick={handleApprove} disabled={actionLoading} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 disabled:opacity-50">
                  Approve
                </button>
                <button onClick={() => setShowRejectModal(true)} disabled={actionLoading} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50">
                  Reject
                </button>
              </>);
            })()}
          </div>
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['overview', 'tasks', 'blockers', 'members'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
            {t === 'blockers' && feature.blockers.filter(b => !b.resolvedAt).length > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {feature.blockers.filter(b => !b.resolvedAt).length}
              </span>
            )}
            {t === 'tasks' && feature.stats.totalTasks > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">{feature.stats.completedTasks}/{feature.stats.totalTasks}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Overview tab ───────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Description</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{feature.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Owner</span>
                <p className="text-gray-600 mt-0.5">{feature.owner?.name || '—'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Members</span>
                <p className="text-gray-600 mt-0.5">{feature.members.length}</p>
              </div>
            </div>
            {feature.tags.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase">Tags</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {feature.tags.map(tag => (
                    <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}
            {feature.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <span className="text-xs font-semibold text-red-600 uppercase">Rejection Reason</span>
                <p className="mt-0.5 text-red-700 text-sm">{feature.rejectionReason}</p>
              </div>
            )}
          </div>
          <AmendmentLog amendments={feature.amendmentLog} />
          <BreachHistory breaches={feature.breachHistory} />
        </div>
      )}

      {/* ── Tasks tab ──────────────────────────────────────────────────── */}
      {tab === 'tasks' && (
        <div className="space-y-3">
          {/* Status banners — explicit per status, no fallthrough */}
          {(!feature.approvalStatus || feature.approvalStatus === 'not_required') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span className="text-xs text-blue-700">This feature has not been submitted for approval. Submit it to get story points reviewed and approved.</span>
            </div>
          )}
          {feature.approvalStatus === 'pending_approval' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <span className="text-xs text-yellow-700">
                {(feature as any).escalatedByName
                  ? `Reviewed by ${(feature as any).escalatedByName} — escalated for final approval. Tasks cannot be marked done until approved.`
                  : 'Submitted for approval — awaiting review. Tasks cannot be marked done until approved.'}
              </span>
            </div>
          )}
          {feature.approvalStatus === 'disputed' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <span className="text-xs text-orange-700">Score disputed — awaiting admin decision. Tasks cannot be marked done until approved.</span>
            </div>
          )}
          {feature.approvalStatus === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <span className="text-xs text-red-700">Feature was rejected. Revise and resubmit. Tasks cannot be marked done until approved.</span>
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
              {(feature.storyPoints ?? 0) > 0 && tasks.length > 0 && (
                <span className="text-xs text-gray-400">
                  {tasks.reduce((s: number, t: any) => s + (t.storyPoints || 0), 0)} / {feature.storyPoints ?? 0} SP assigned
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {tasks.length > 0 && tasks.some((t: any) => !t.storyPoints || t.storyPoints <= 0) && (
                <button
                  onClick={async () => {
                    setAiEstimating(true);
                    setAiResult(null);
                    setAiFallback(false);
                    try {
                      const res = await aiAPI.estimateStoryPoints(featureId!, true);
                      const d = res.data;
                      if (d.fallback) {
                        setAiFallback(true);
                        setAiMessage(d.message || 'AI unavailable');
                      } else if (d.data) {
                        setAiResult(d.data);
                        fetchTasks();
                        fetchFeature();
                      }
                    } catch {
                      setAiFallback(true);
                      setAiMessage('AI estimation failed');
                    }
                    setAiEstimating(false);
                  }}
                  disabled={aiEstimating}
                  className="text-xs border border-purple-200 text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  title="Use AI to estimate story points for tasks without SP"
                >
                  {aiEstimating ? (
                    <>
                      <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      Estimating...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
                      Estimate SP
                    </>
                  )}
                </button>
              )}
              {(feature.storyPoints ?? 0) > 0 && tasks.length > 0 && (
                <button
                  onClick={async () => {
                    const perTask = Math.floor((feature.storyPoints ?? 0) / tasks.length);
                    const remainder = (feature.storyPoints ?? 0) % tasks.length;
                    for (let i = 0; i < tasks.length; i++) {
                      const pts = perTask + (i < remainder ? 1 : 0);
                      if (pts !== (tasks[i].storyPoints || 0)) {
                        await taskAPI.updateTask(tasks[i]._id, { storyPoints: pts } as any);
                      }
                    }
                    fetchTasks();
                  }}
                  className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50"
                  title="Split feature story points equally across all tasks"
                >
                  Distribute Equally
                </button>
              )}
              <button
                onClick={() => setShowCreateTask(!showCreateTask)}
                className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700"
              >
                {showCreateTask ? 'Cancel' : '+ Create Task'}
              </button>
            </div>
          </div>

          {/* AI estimation result/fallback inline banner */}
          {aiFallback && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <span className="text-xs text-yellow-700">{aiMessage || 'AI estimation not available — assign points manually'}</span>
              <button onClick={() => setAiFallback(false)} className="text-yellow-500 hover:text-yellow-700 ml-auto shrink-0">&times;</button>
            </div>
          )}
          {aiResult && !aiFallback && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-purple-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
              <span className="text-xs text-purple-700">AI estimated {aiResult.featurePoints} SP total — {aiResult.tasks.length} tasks updated</span>
              <button onClick={() => setAiResult(null)} className="text-purple-400 hover:text-purple-600 ml-auto shrink-0">&times;</button>
            </div>
          )}

          {/* Inline create task form */}
          {showCreateTask && (
            <form onSubmit={handleCreateTask} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <input
                value={newTask.title}
                onChange={e => setNewTask(f => ({ ...f, title: e.target.value }))}
                placeholder="Task title *"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-3 flex-wrap">
                <select
                  value={newTask.priority}
                  onChange={e => setNewTask(f => ({ ...f, priority: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <input
                  type="number" min={0}
                  value={newTask.storyPoints}
                  onChange={e => setNewTask(f => ({ ...f, storyPoints: Number(e.target.value) }))}
                  placeholder="SP"
                  className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Story Points"
                />
                <input
                  value={newTask.description}
                  onChange={e => setNewTask(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={creatingTask || !newTask.title.trim()}
                  className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {creatingTask ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          )}

          {tasksLoading ? (
            <div className="flex justify-center p-8"><div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-8">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700">No tasks yet</p>
              <p className="text-xs text-gray-400 mt-1">Create tasks to break this feature into actionable work items.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {tasks.map((task: any, idx: number) => (
                <div key={task._id} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-400 mt-3 w-5 text-right shrink-0">{idx + 1}.</span>
                  <div className="flex-1"><TaskRow task={task} onRefresh={fetchTasks} featureId={featureId!} featureApproved={feature.approvalStatus === 'approved' || feature.approvalStatus === 'not_required'} /></div>
                </div>
              ))}
            </div>
          )}

          {/* Batch submission for tasks needing SP */}
          <FeatureBatchEstimation
            featureId={featureId!}
            featureName={feature.name}
            projectId={feature.projectId}
            tasks={tasks}
            featureApproved={feature.approvalStatus === 'approved'}
            onRefresh={() => { fetchTasks(); fetchFeature(); }}
          />
        </div>
      )}

      {/* ── Blockers tab ───────────────────────────────────────────────── */}
      {tab === 'blockers' && (
        <div className="space-y-4">
          {feature.blockers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm font-medium">No blockers recorded</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {feature.blockers.map((blocker: FeatureBlocker, idx: number) => (
                <div key={idx} className={`bg-white border rounded-lg px-4 py-3 ${blocker.resolvedAt ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BLOCKER_TYPE_COLORS[blocker.type] || 'bg-gray-100 text-gray-700'}`}>
                          {blocker.type}
                        </span>
                        {blocker.resolvedAt ? (
                          <span className="text-xs text-green-600 font-medium">Resolved {new Date(blocker.resolvedAt).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-xs text-red-600 font-medium">Blocking</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700">{blocker.description}</p>
                    </div>
                    {!blocker.resolvedAt && (
                      <button onClick={() => handleResolveBlocker(idx)} className="text-xs font-medium text-green-600 hover:text-green-800 shrink-0">
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleAddBlocker} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Add Blocker</h3>
            <div className="flex gap-3">
              <select
                value={blockerForm.type}
                onChange={e => setBlockerForm(f => ({ ...f, type: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              >
                <option value="task">Task</option>
                <option value="feature">Feature</option>
                <option value="bug">Bug</option>
                <option value="external">External</option>
              </select>
              <input
                value={blockerForm.description}
                onChange={e => setBlockerForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the blocker..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={blockerSubmitting || !blockerForm.description.trim()}
                className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Members tab ────────────────────────────────────────────────── */}
      {tab === 'members' && (
        <div className="space-y-4">
          {feature.members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm font-medium">No members added yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {feature.members.map((member: FeatureMember) => (
                <div key={member.userId} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{member.name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${MEMBER_ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-600'}`}>
                        {member.role}
                      </span>
                    </div>
                    {member.role !== 'owner' && (
                      <button onClick={() => handleRemoveMember(member.userId)} className="text-xs text-red-500 hover:text-red-700">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add member — search project members by name/email */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Add Member</h3>
            <div className="flex gap-3 items-end">
              <div className="flex-1 relative">
                <input
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {memberSearch && filteredMembers.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredMembers.map(u => (
                      <button
                        key={u._id}
                        type="button"
                        onClick={() => handleAddMember(u as any)}
                        disabled={memberSubmitting}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-3"
                      >
                        <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 truncate">{u.name}</div>
                          {u.email && <div className="text-xs text-gray-400 truncate">{u.email}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {memberSearch && filteredMembers.length === 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-500 text-center">
                    No matching project members found
                  </div>
                )}
              </div>
              <select
                value={memberRole}
                onChange={e => setMemberRole(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
              >
                <option value="contributor">Contributor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <p className="text-xs text-gray-400">Select from project members. Type to search by name or email.</p>
          </div>
        </div>
      )}

      {/* ── Approval panel — review story points before submitting ────── */}
      {showApprovalPanel && (() => {
        const currentFeatureSP = editFeatureSP || (feature.storyPoints ?? 0);
        const includedTasks = tasks.filter((t: any) => !(t as any)._excluded);
        const taskSPSum = includedTasks.reduce((s: number, t: any) => s + ((t as any)._editSP ?? t.storyPoints ?? 0), 0);
        const overBudget = currentFeatureSP > 0 && taskSPSum > currentFeatureSP;
        const ready = currentFeatureSP > 0 && tasks.length > 0 && !overBudget;

        // Locked: always locked unless admin approved manual override. Also locked during AI loading.
        // Locked unless admin granted manual override. AI loading doesn't lock when manual override is on.
        const isAiLocked = !feature.manualOverride;

        // Build per-member summary
        const memberPoints: Record<string, { name: string; points: number; tasks: number }> = {};
        for (const t of includedTasks) {
          const sp = (t as any)._editSP ?? t.storyPoints ?? 0;
          const assignees = t.assignedTo ?? [];
          if (assignees.length === 0) {
            const k = '__unassigned';
            if (!memberPoints[k]) memberPoints[k] = { name: 'Unassigned', points: 0, tasks: 0 };
            memberPoints[k].points += sp;
            memberPoints[k].tasks += 1;
          } else {
            for (const a of assignees) {
              const k = a.userId || a.name;
              if (!memberPoints[k]) memberPoints[k] = { name: a.name || 'Unknown', points: 0, tasks: 0 };
              memberPoints[k].points += sp;
              memberPoints[k].tasks += 1;
            }
          }
        }

        return (
          <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[85vh] overflow-hidden flex">
              {/* Left — main panel */}
              <div className="flex-1 p-6 overflow-y-auto space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-lg">Submit for Approval</h3>
                  {!aiEstimating && !isAiLocked && (
                    <button
                      onClick={async () => {
                        setAiEstimating(true); setAiResult(null); setAiFallback(false); setAiMessage('');
                        try {
                          const res = await aiAPI.estimateStoryPoints(featureId!, true); // force=true bypasses cache
                          const d = res.data;
                          if (d.fallback) { setAiFallback(true); setAiMessage(d.message); }
                          else if (d.data) {
                            setAiResult(d.data); setEditFeatureSP(d.data.featurePoints);
                            setTasks((prev: any[]) => prev.map((t: any) => { const at = d.data.tasks.find((a: any) => a.taskId === t._id); return at ? { ...t, _editSP: at.points, _aiReasoning: at.reasoning } : t; }));
                          }
                        } catch { setAiFallback(true); setAiMessage('AI failed'); }
                        setAiEstimating(false);
                      }}
                      className="flex items-center gap-1 text-xs border border-purple-200 text-purple-600 px-2.5 py-1 rounded-lg hover:bg-purple-50"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                      Re-estimate with AI
                    </button>
                  )}
                </div>

                {/* AI status */}
                {aiEstimating && (
                  <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg p-3">
                    <svg className="w-4 h-4 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    <span className="text-xs text-purple-700">AI is estimating story points...</span>
                  </div>
                )}
                {!aiEstimating && aiResult && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg p-3">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                    <span className="text-xs text-green-700">
                      AI estimated {aiResult.featurePoints} SP
                      {feature.aiEstimation?.estimatedAt && ` on ${new Date(feature.aiEstimation.estimatedAt).toLocaleDateString()}`}
                      {isAiLocked ? ' — scores are locked' : ' — you can adjust below'}
                    </span>
                  </div>
                )}
                {!aiEstimating && aiFallback && (
                  <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-100 rounded-lg p-3">
                    <svg className="w-4 h-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                    <span className="text-xs text-yellow-700">{aiMessage || 'AI unavailable'} — assign points manually</span>
                  </div>
                )}

                {/* Feature SP */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Feature Story Points</span>
                    <div className="flex items-center gap-2">
                      <input type="number" min={0} value={editFeatureSP === 0 ? '' : editFeatureSP} onChange={e => setEditFeatureSP(e.target.value === '' ? 0 : Number(e.target.value))} onFocus={e => { if (e.target.value === '0') e.target.value = ''; }} disabled={!feature.manualOverride || aiEstimating} className={`w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 ${!feature.manualOverride || aiEstimating ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                      <span className="text-xs text-gray-400">SP</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Tasks: {taskSPSum} / {currentFeatureSP} SP</span>
                    {overBudget && <span className="text-red-600 font-medium">Over by {taskSPSum - currentFeatureSP}</span>}
                    {!overBudget && taskSPSum === currentFeatureSP && currentFeatureSP > 0 && <span className="text-green-600">All assigned</span>}
                    {!overBudget && taskSPSum < currentFeatureSP && currentFeatureSP > 0 && <span className="text-gray-400">{currentFeatureSP - taskSPSum} remaining</span>}
                  </div>
                  {currentFeatureSP > 0 && (
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${overBudget ? 'bg-red-500' : taskSPSum === currentFeatureSP ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, currentFeatureSP > 0 ? (taskSPSum / currentFeatureSP) * 100 : 0)}%` }} />
                    </div>
                  )}
                </div>

                {/* Tasks with sliders + assignment */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Tasks ({tasks.length}) {isAiLocked && <span className="text-xs text-purple-500 font-normal ml-1">— AI assigned (read-only)</span>}</span>
                    {!isAiLocked && (
                      <div className="flex gap-2">
                        {currentFeatureSP > 0 && (
                          <button onClick={() => {
                            const lockedSum = tasks.reduce((s: number, t: any) => s + ((t as any)._locked || (t as any)._excluded ? ((t as any)._editSP ?? t.storyPoints ?? 0) : 0), 0);
                            const pool = Math.max(0, currentFeatureSP - lockedSum);
                            const unlocked = tasks.filter((t: any) => !(t as any)._locked && !(t as any)._excluded);
                            if (!unlocked.length) return;
                            const per = Math.floor(pool / unlocked.length); const rem = pool % unlocked.length; let idx = 0;
                            setTasks(prev => prev.map((t: any) => { if ((t as any)._excluded) return { ...t, _editSP: 0 }; if ((t as any)._locked) return t; return { ...t, _editSP: per + (idx++ < rem ? 1 : 0) }; }));
                          }} className="text-xs border border-blue-200 text-blue-600 px-2 py-0.5 rounded hover:bg-blue-50">Distribute Equally</button>
                        )}
                        <button onClick={() => setTasks(prev => prev.map((t: any) => ({ ...t, _locked: false })))} className="text-xs text-gray-500 hover:text-gray-700">Unlock All</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {tasks.map((t: any, taskIdx: number) => {
                      const sp = (t as any)._editSP ?? t.storyPoints ?? 0;
                      const excluded = !!(t as any)._excluded;
                      const locked = !!(t as any)._locked;
                      const pct = currentFeatureSP > 0 ? Math.round((sp / currentFeatureSP) * 100) : 0;
                      const othersLockedSum = tasks.reduce((s: number, pt: any) => { if (pt._id === t._id) return s; if ((pt as any)._locked || (pt as any)._excluded) return s + ((pt as any)._editSP ?? pt.storyPoints ?? 0); return s; }, 0);
                      const sliderMax = Math.max(0, currentFeatureSP - othersLockedSum);
                      const assignees = t.assignedTo ?? [];

                      const handleSliderChange = (val: number) => {
                        if (locked || excluded) return;
                        const capped = Math.min(val, sliderMax);
                        if (capped === sp) return;
                        setTasks(prev => {
                          const next = prev.map((pt: any) => ({ ...pt }));
                          const me = next.find((pt: any) => pt._id === t._id); if (!me) return prev;
                          me._editSP = capped;
                          const peers = next.filter((pt: any) => pt._id !== t._id && !pt._locked && !pt._excluded);
                          if (!peers.length) return next;
                          const fixedSum = next.reduce((s: number, pt: any) => s + (pt._locked || pt._excluded || pt._id === t._id ? (pt._editSP ?? pt.storyPoints ?? 0) : 0), 0);
                          const remaining = Math.max(0, currentFeatureSP - fixedSum);
                          const peerTotal = peers.reduce((s: number, pt: any) => s + (pt._editSP ?? pt.storyPoints ?? 0), 0);
                          if (peerTotal === 0) { const each = Math.floor(remaining / peers.length); const r = remaining % peers.length; peers.forEach((pt: any, i: number) => { pt._editSP = each + (i < r ? 1 : 0); }); }
                          else { let d = 0; peers.forEach((pt: any, i: number) => { if (i === peers.length - 1) pt._editSP = Math.max(0, remaining - d); else { const share = Math.round(((pt._editSP ?? pt.storyPoints ?? 0) / peerTotal) * remaining); pt._editSP = Math.max(0, share); d += pt._editSP; } }); }
                          return next;
                        });
                      };

                      return (
                        <div key={t._id} className={`py-2 px-2 rounded ${excluded ? 'opacity-40' : ''}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-400 w-4 text-right shrink-0">{taskIdx + 1}.</span>
                            {!isAiLocked && <input type="checkbox" checked={!excluded} onChange={() => setTasks(prev => prev.map((pt: any) => pt._id === t._id ? { ...pt, _excluded: !excluded, _editSP: !excluded ? 0 : pt._editSP, _locked: false } : pt))} className="w-3.5 h-3.5 text-blue-600 rounded shrink-0" />}
                            {!excluded && !isAiLocked && (
                              <button onClick={() => setTasks(prev => prev.map((pt: any) => pt._id === t._id ? { ...pt, _locked: !locked } : pt))} className={`shrink-0 ${locked ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500'}`} title={locked ? 'Unlock' : 'Lock'}>
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">{locked ? <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /> : <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />}</svg>
                              </button>
                            )}
                            <span className={`text-xs truncate flex-1 ${t.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-700'}`} title={`${t.title}${t.description ? '\n' + t.description : ''}`}>{t.title}</span>
                            {/* Quick assign — show avatars or assign button */}
                            {assignees.length > 0 ? (
                              <div className="flex -space-x-1 shrink-0">
                                {assignees.slice(0, 3).map((a: any, i: number) => (
                                  <div key={i} className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center border border-white" title={a.name}>{(a.name || '?')[0]}</div>
                                ))}
                                {assignees.length > 3 && <div className="w-5 h-5 rounded-full bg-gray-300 text-gray-600 text-xs flex items-center justify-center border border-white">+{assignees.length - 3}</div>}
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  // Auto-assign to current user (submitter)
                                  const me = { userId: feature.owner?.userId, name: feature.owner?.name, assignedAt: new Date().toISOString() };
                                  taskAPI.assignTask(t._id, me.userId).then(() => fetchTasks()).catch(() => {});
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700 shrink-0"
                                title="Assign to yourself"
                              >
                                Assign
                              </button>
                            )}
                            <span className={`text-xs font-medium w-10 text-right ${locked ? 'text-amber-600' : 'text-blue-600'}`}>{sp}</span>
                          </div>
                          {!excluded && currentFeatureSP > 0 && (
                            <div className="flex items-center gap-2 ml-10">
                              <input type="range" min={0} max={sliderMax} value={Math.min(sp, sliderMax)} onChange={e => handleSliderChange(Number(e.target.value))} disabled={locked || isAiLocked} className={`flex-1 h-1.5 cursor-pointer ${locked || isAiLocked ? 'accent-gray-400 opacity-60' : 'accent-blue-600'}`} title={isAiLocked ? 'AI assigned — read only' : `Max: ${sliderMax} SP`} />
                              <span className="text-xs text-gray-400 w-7 text-right">{pct}%</span>
                            </div>
                          )}
                          {(t as any)._aiReasoning && (
                            <div className="ml-10 mt-0.5 flex items-start gap-1">
                              <svg className="w-3 h-3 text-purple-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.158 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                              <span className="text-xs text-purple-500 italic">{(t as any)._aiReasoning}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Per-member summary */}
                {Object.keys(memberPoints).length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Points per Member</h4>
                    <div className="space-y-1">
                      {Object.values(memberPoints).sort((a, b) => b.points - a.points).map((m, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">{m.name[0]}</div>
                            <span className="text-gray-700">{m.name}</span>
                            <span className="text-gray-400">{m.tasks} task{m.tasks !== 1 ? 's' : ''}</span>
                          </div>
                          <span className="font-medium text-blue-600">{m.points} SP</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {overBudget && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">Task SP ({taskSPSum}) exceed feature total ({currentFeatureSP}).</div>}
                {!ready && !overBudget && !aiEstimating && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-700">Feature needs story points (&gt; 0) and at least one task.</div>}

                {/* Dispute section — only when AI locked */}
                {isAiLocked && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!(disputeText)}
                        onChange={e => setDisputeText(e.target.checked ? ' ' : '')}
                        className="mt-0.5 w-3.5 h-3.5 text-orange-600 rounded"
                      />
                      <span className="text-xs text-orange-700">I disagree with the AI estimation and want to raise a dispute</span>
                    </label>
                    {disputeText && (
                      <textarea
                        value={disputeText.trim() ? disputeText : ''}
                        onChange={e => setDisputeText(e.target.value)}
                        placeholder="Explain why you disagree with the AI scores..."
                        rows={2}
                        className="w-full border border-orange-300 rounded px-3 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    )}
                  </div>
                )}

                {/* Submit buttons */}
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setShowApprovalPanel(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                  <button
                    disabled={actionLoading || !ready || aiEstimating}
                    onClick={async () => {
                      // If disputing, submit for approval first, then dispute
                      if (isAiLocked && disputeText?.trim()) {
                        try {
                          // Submit first so it enters pending_approval
                          await featureAPI.submitForApproval(featureId!);
                          // Then dispute
                          await featureAPI.dispute(featureId!, { reason: disputeText.trim() });
                          setShowApprovalPanel(false);
                          setDisputeText('');
                          await fetchFeature();
                        } catch (err: any) { setError(err.response?.data?.message || 'Failed'); }
                        return;
                      }

                      // Manual mode — save edited SPs
                      if (!isAiLocked) {
                        for (const t of tasks) { const newSP = (t as any)._editSP; if (newSP !== undefined && newSP !== (t.storyPoints ?? 0)) await taskAPI.updateTask(t._id, { storyPoints: newSP } as any); }
                        const newFeatureSP = editFeatureSP || (feature.storyPoints ?? 0);
                        if (newFeatureSP !== (feature.storyPoints ?? 0)) await featureAPI.update(featureId!, { storyPoints: newFeatureSP } as any);
                      }

                      await handleSubmitForApproval();
                      setShowApprovalPanel(false);
                      setDisputeText('');
                      await fetchFeature();
                      fetchTasks();
                    }}
                    className={`px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${
                      disputeText?.trim() ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-yellow-500 text-white hover:bg-yellow-600'
                    }`}
                  >
                    {actionLoading ? 'Submitting…' : disputeText?.trim() ? 'Submit with Dispute' : isAiLocked ? 'Accept & Submit' : 'Submit for Approval'}
                  </button>
                </div>
              </div>

              {/* Right — info sidebar */}
              <div className="w-72 bg-blue-50 border-l border-blue-100 p-5 overflow-y-auto shrink-0 hidden lg:block">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">What are Story Points?</h4>
                <div className="space-y-3 text-xs text-blue-800 leading-relaxed">
                  <p>Story points measure the <strong>effort, complexity, and risk</strong> of a task — not hours. They help teams estimate work consistently.</p>
                  <div>
                    <h5 className="font-semibold mb-1">Fibonacci Scale (recommended)</h5>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="bg-white/60 rounded px-2 py-1"><strong>1</strong> — Trivial, few minutes</div>
                      <div className="bg-white/60 rounded px-2 py-1"><strong>2</strong> — Simple, clear path</div>
                      <div className="bg-white/60 rounded px-2 py-1"><strong>3</strong> — Small task, minor unknowns</div>
                      <div className="bg-white/60 rounded px-2 py-1"><strong>5</strong> — Medium, some complexity</div>
                      <div className="bg-white/60 rounded px-2 py-1"><strong>8</strong> — Large, multiple parts</div>
                      <div className="bg-white/60 rounded px-2 py-1"><strong>13</strong> — Very large, consider splitting</div>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-1">Tips</h5>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Compare tasks relative to each other, not to hours</li>
                      <li>A "3" should always feel similar across your team</li>
                      <li>If a task is 13+, break it into subtasks</li>
                      <li>Points earned = actual work done by assignee</li>
                      <li>Lock tasks you're confident about, distribute the rest</li>
                    </ul>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <h5 className="font-semibold mb-1">How scoring works</h5>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Each task assignee earns its full SP when completed</li>
                      <li>Multiple assignees each get the full task SP</li>
                      <li>Points accumulate monthly</li>
                      <li>Admin approves monthly scores</li>
                      <li>Unassigned tasks — <strong>no one earns</strong> until assigned</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Reject modal ───────────────────────────────────────────────── */}
      {showRejectModal && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold text-gray-900">Reject Feature</h3>
            <textarea
              placeholder="Reason for rejection *"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading}
                className="bg-red-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureDetail;
